interface ClinicalSummaryReplacePromptProps {
  orphanCount: number
  /** Clinician-owned terms the new text no longer supports; named in the copy when above zero. */
  keptCount?: number
  onClearOrphans: () => void
  onKeepEverything: () => void
  onUndoReplacement: () => void
}

/** Shown once a largely replaced summary is extracted: the terms only the previous text supported need a decision. */
export function ClinicalSummaryReplacePrompt({
  orphanCount,
  keptCount = 0,
  onClearOrphans,
  onKeepEverything,
  onUndoReplacement,
}: ClinicalSummaryReplacePromptProps) {
  if (orphanCount <= 0) return null

  return (
    <div
      className="flex flex-col rounded-base border border-border-base dark:border-gray-700 bg-background-brand-softer dark:bg-gray-800 px-4 py-3.5"
      role="region"
      aria-label="Summary replacement prompt"
      aria-live="polite"
      data-testid="clinical-summary-replace-prompt"
    >
      <p className="text-sm font-medium leading-5 text-text-heading dark:text-white">
        You replaced most of the summary.
      </p>
      <p className="text-sm leading-5 text-text-body dark:text-gray-400">
        {orphanCount} phenotype{orphanCount === 1 ? '' : 's'} came only from the previous text.
        {keptCount > 0
          ? ` The ${keptCount} you added or decided on ${keptCount === 1 ? 'is' : 'are'} kept either way.`
          : null}
      </p>
      <div className="mt-2.5 flex min-h-[30px] flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onClearOrphans}
          className="rounded-full bg-background-brand-strong px-2.5 py-1 text-xs font-medium leading-4 text-white hover:bg-brand-800"
        >
          Clear {orphanCount === 1 ? 'that' : 'those'} {orphanCount}
          {keptCount > 0 ? `, keep your ${keptCount}` : null}
        </button>
        <button
          type="button"
          onClick={onKeepEverything}
          className="rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium leading-4 text-text-heading dark:text-white hover:bg-background-gray dark:hover:bg-gray-800"
        >
          Keep everything
        </button>
        <button
          type="button"
          onClick={onUndoReplacement}
          className="ml-auto text-xs font-medium leading-5 text-text-fg-brand dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
        >
          Undo the replacement
        </button>
      </div>
    </div>
  )
}
